import { useState } from "react";
import {
  List,
  CreateButton,
  DataTable,
  ReferenceField,
} from "@/components/admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRecordContext } from "ra-core";
import {
  Briefcase,
  Activity,
  LayoutGrid,
  List as ListIcon,
  MapPin,
  DollarSign,
  Calendar,
  User,
} from "lucide-react";
import { JobKanban } from "./JobKanban";
import {
  JOB_TYPES,
  getJobTypeLabel,
  getJobTypeBadgeClass,
  getJobTypeIcon,
} from "../constants";
import { JOB_STAGES, findStageLabel, findStageColor } from "./jobStages";
import type { Job } from "../../providers/fakerest/dataGenerator/types";
import {
  FilterSidebar,
  FilterButton,
  type FilterSection,
  type FilterOption,
} from "../../filters/FilterSidebar";
import { useFilterSidebar } from "../../filters/useFilterSidebar";
import { TopToolbar } from "../../layout/TopToolbar";

// Column header with icon helper
const ColHeader = ({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) => (
  <span className="flex items-center gap-1.5">
    <Icon className="h-4 w-4 text-muted-foreground" />
    {label}
  </span>
);

// Job type badge renderer
const JobTypeBadge = (record: Job) => {
  const Icon = getJobTypeIcon(record.job_type);
  return (
    <Badge
      className={`${getJobTypeBadgeClass(record.job_type)} flex items-center gap-1.5`}
    >
      <Icon className="h-3 w-3" />
      {getJobTypeLabel(record.job_type)}
    </Badge>
  );
};

// Status badge renderer
const StatusBadge = (record: Job) => {
  const color = findStageColor(record.status);
  const label = findStageLabel(record.status);
  return (
    <Badge
      className={`${color} text-white flex items-center gap-1.5 capitalize`}
    >
      {label}
    </Badge>
  );
};

// Currency formatter
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const EstimatedValueCell = (record: Job) => (
  <span className="font-medium">{formatCurrency(record.estimated_value)}</span>
);

// Contact name from reference
const ContactName = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span>{record.name}</span>;
};

// Build filter sections for jobs
const jobTypeSection: FilterSection = {
  id: "job_type",
  label: "Job Type",
  icon: <Briefcase className="h-4 w-4" />,
  type: "multi",
  source: "job_type",
  options: JOB_TYPES.map((jobType) => ({
    value: jobType.id,
    label: jobType.label,
    icon: <jobType.icon className={`h-4 w-4 ${jobType.color}`} />,
  })),
};

const statusSection: FilterSection = {
  id: "status",
  label: "Status",
  icon: <Activity className="h-4 w-4" />,
  type: "multi",
  source: "status",
  options: JOB_STAGES.map((stage) => ({
    value: stage.value,
    label: stage.label,
    color: stage.color.replace("bg-", "text-").replace("-500", "-600"),
  })),
};

const filterSections: FilterSection[] = [jobTypeSection, statusSection];

const sortOptions: FilterOption[] = [
  { value: "created_at:DESC", label: "Newest first" },
  { value: "created_at:ASC", label: "Oldest first" },
  { value: "estimated_value:DESC", label: "Highest value" },
  { value: "estimated_value:ASC", label: "Lowest value" },
  { value: "updated_at:DESC", label: "Recently updated" },
];

type ViewMode = "kanban" | "list";

const ViewToggle = ({
  view,
  onViewChange,
}: {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
}) => (
  <div className="flex items-center border rounded-md">
    <Button
      variant={view === "kanban" ? "secondary" : "ghost"}
      size="sm"
      className="rounded-r-none px-3"
      onClick={() => onViewChange("kanban")}
    >
      <LayoutGrid className="h-4 w-4" />
    </Button>
    <Button
      variant={view === "list" ? "secondary" : "ghost"}
      size="sm"
      className="rounded-l-none px-3"
      onClick={() => onViewChange("list")}
    >
      <ListIcon className="h-4 w-4" />
    </Button>
  </div>
);

const JobListActions = ({
  view,
  onViewChange,
}: {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
}) => {
  const { open, setOpen, activeFilterCount } = useFilterSidebar();

  return (
    <>
      <TopToolbar>
        <ViewToggle view={view} onViewChange={onViewChange} />
        <FilterButton
          onClick={() => setOpen(true)}
          activeCount={activeFilterCount}
        />
        <CreateButton />
      </TopToolbar>
      <FilterSidebar
        open={open}
        onOpenChange={setOpen}
        sections={filterSections}
        sortOptions={sortOptions}
      />
    </>
  );
};

// Table view for jobs
const JobTableView = () => (
  <DataTable<Job> rowClick="show">
    <DataTable.Col
      source="address"
      label={<ColHeader icon={MapPin} label="Address" />}
      headerClassName="w-[300px]"
      cellClassName="w-[300px]"
    />
    <DataTable.Col
      label={<ColHeader icon={User} label="Customer" />}
      headerClassName="w-[180px]"
      cellClassName="w-[180px]"
    >
      <ReferenceField source="contact_id" reference="contacts" link={false}>
        <ContactName />
      </ReferenceField>
    </DataTable.Col>
    <DataTable.Col
      source="job_type"
      label={<ColHeader icon={Briefcase} label="Type" />}
      render={JobTypeBadge}
      headerClassName="w-[150px]"
      cellClassName="w-[150px]"
    />
    <DataTable.Col
      source="status"
      label={<ColHeader icon={Activity} label="Status" />}
      render={StatusBadge}
      headerClassName="w-[150px]"
      cellClassName="w-[150px]"
    />
    <DataTable.Col
      source="estimated_value"
      label={<ColHeader icon={DollarSign} label="Value" />}
      render={EstimatedValueCell}
      headerClassName="text-right w-[120px]"
      cellClassName="text-right w-[120px]"
    />
    <DataTable.Col
      source="created_at"
      label={<ColHeader icon={Calendar} label="Created" />}
      headerClassName="w-[120px]"
      cellClassName="w-[120px]"
    />
  </DataTable>
);

export const JobList = () => {
  const [view, setView] = useState<ViewMode>("kanban");

  return (
    <List
      perPage={1000}
      pagination={false}
      actions={<JobListActions view={view} onViewChange={setView} />}
    >
      {view === "kanban" ? <JobKanban /> : <JobTableView />}
    </List>
  );
};
